import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalizedStrings } from '../Localization';
import { usePost } from '../API';
import {
  useToast,
  Button,
  Alert,
  Spinner,
  SidebarNavProvider,
  SidebarNav,
  SidebarNavContent,
  SidebarNavMenu,
  SidebarNavMenuItem,
  SidebarNavMenuButton,
  Icon,
  Modal,
  Heading,
  type IconProps
} from '@aidkitorg/component-library';
import NotificationsWorkflowReview from './NotificationsWorkflowReview';
import { useCollabActions } from '../CollabWrapper';
import { generateRandomString } from '../Util';
import { UserActivity } from '@aidkitorg/typesheets/lib/collab';
import { ChangeSet } from '@aidkitorg/typesheets/lib/collab';
import DistroReviewSummary from './DistroReviewSummary';
import DistroReviewDescription from './DistroReviewDescription';
import { ReviewData } from '@aidkitorg/aidkit/lib/distro/review/distroReview';
import { ChangesWorkflowReview } from './ChangesWorkflowReview';
import TargetFieldsWorkflowReview from './TargetFieldsWorkflowReview';

type ConfigReviewParams = {
  id: string;
};

type View = 'summary' | 'notifications' | 'changes' | 'target_fields';

// Polling interval in milliseconds (5 seconds)
const POLLING_INTERVAL = 5000;

const getViewFromHash = (): View => {
  const hash = window.location.hash.replace('#', '');
  const validViews: View[] = ['summary', 'notifications', 'changes', 'target_fields'];
  return validViews.includes(hash as View) ? (hash as View) : 'summary';
};

const setHashForView = (view: View) => {
  if (view === 'summary') {
    window.location.hash = '';
  } else {
    window.location.hash = view;
  }
};

const getWorkflowStatus = (workflowStatuses: ReviewData['workflowStatuses'], workflowName: string) => {
  return workflowStatuses?.find(w => w.workflowName === workflowName)?.status || 'pending';
};

const getStatusIcon = (status: string): React.ReactNode | null => {
  const baseProps = {
    type: 'outline' as const,
    className: "w-5 h-5 text-gray-500",
    strokeWidth: 2,
    role: 'informative' as const,
  };

  switch (status) {
    case 'completed':
      return null;
    case 'failed':
    case 'cancelled':
      return <Icon {...{ 
        ...baseProps,
        name: 'XMarkIcon',
        className: "w-5 h-5 text-red-500",
        title: status === 'failed' ? 'Workflow failed' : 'Workflow cancelled',
      }} />;
    default:
      return <Icon {...{ 
        ...baseProps,
        name: 'ClockIcon',
        title: 'Workflow pending',
      }} />;
  }
};

export default function DistroReviewPage() {
  const { id: reviewId } = useParams<ConfigReviewParams>();
  const L = useLocalizedStrings();
  const getDistroReview = usePost('/distro-review/get');
  const loadSurveyHash = usePost('/survey/load_survey_metadata');
  const publishDistroReview = usePost('/distro-review/publish');
  const closeDistroReview = usePost('/distro-review/close');
  const approveDistroReview = usePost('/distro-review/approve');
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [currentSurveyHash, setCurrentSurveyHash] = useState<string | null>(null);
  const [surveyHashMismatch, setSurveyHashMismatch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showForcePublishConfirmation, setShowForcePublishConfirmation] = useState(false);
  const [currentView, setCurrentView] = useState<View>(getViewFromHash());
  const { toast } = useToast();

  // Determine if this review has notification changes that require attention
  const reviewRequiredResult = reviewData?.reviewRequiredResult;
  const hasNotificationChanges = reviewRequiredResult?.requiresReview &&
    (reviewRequiredResult.reasons.notifications?.directlyChanged?.length ||
     reviewRequiredResult.reasons.notifications?.affectedByFieldChanges?.length);

  // Collab stuff
  const tabId = useRef(generateRandomString(4, 'abcdefghjkmnpqrstuvwxyz123456789'));
  const { collabEnabled } = useCollabActions(
    reviewData?.surveyName || 'entireprogram',
    tabId.current,
    (events: ChangeSet, userActivity?: UserActivity[]) => {},
    false
  );

  // Handle URL hash changes
  useEffect(() => {
    setHashForView(getViewFromHash()); // replaces hash with default if invalid
    const handleHashChange = () => {
      setHashForView(getViewFromHash());
      setCurrentView(getViewFromHash());
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const isPublished = reviewData?.status === 'published';
  const isClosed = reviewData?.status === 'closed';
  const isPendingReview = reviewData?.status === 'review_pending';
  const isApproved = reviewData?.status === 'approved';

  useEffect(() => {
    const loadSurvey = async () => {
      setIsLoading(true);
      try {
        const result = await getDistroReview({ reviewId });
        if (!('error' in result)) {
          setReviewData(result);
        }
        checkSurveyHashChange();
      } catch (error) {
        console.error('Error loading review:', error);
        toast({
          description: 'Failed to load survey review',
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (reviewId) {
      loadSurvey();
    }
  }, [reviewId, getDistroReview, toast]);

  const refreshReviewData = useCallback(async () => {
    if (!reviewId) return;
    try {
      const result = await getDistroReview({ reviewId });
      if (!('error' in result)) {
        setReviewData(result);
      }
    } catch (error) {
      console.error('Error refreshing review data:', error);
      toast({
        description: 'Failed to refresh review data',
        variant: 'error'
      });
    }
  }, [reviewId, getDistroReview, toast]);

  const checkSurveyHashChange = useCallback(async () => {
    if (!reviewData?.surveyName || !reviewData?.currentSurveyHash) return;
    
    try {
      const metadataResponse = await loadSurveyHash({ 
        name: reviewData.surveyName, 
        purpose: "DistroReviewPage polling for changes" 
      });
      
      if (metadataResponse && metadataResponse.hash) {
        setCurrentSurveyHash(metadataResponse.hash);
        setSurveyHashMismatch(isPublished ? metadataResponse.hash !== reviewData.surveyHash : (metadataResponse.hash !== reviewData.currentSurveyHash && metadataResponse.hash !== reviewData.surveyHash));
      }
    } catch (error) {
      console.error('Error checking survey hash:', error);
    }
  }, [reviewData?.surveyName, reviewData?.currentSurveyHash, loadSurveyHash]);
  useEffect(() => {
    if (!reviewId) {
      return;
    }
    checkSurveyHashChange();
  }, [reviewData]);

  // Polling effect
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!reviewId || isPublishing) {
      return;
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        if (!isPublished && !isClosed) {
          await refreshReviewData();
        }
        await checkSurveyHashChange();
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, POLLING_INTERVAL);


    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [reviewId, isPublished, isClosed, refreshReviewData, checkSurveyHashChange]);

  const handleApprove = async () => {
    setIsPublishing(true);
    try {
      const result = await approveDistroReview({ reviewId });
      if (!result.error) {
        toast({
          description: 'Review approved successfully',
          variant: 'success',
          duration: 5000
        });
        await refreshReviewData();
      }
    } catch (error) {
      console.error('Error approving review:', error);
      toast({
        description: 'Failed to approve review',
        variant: 'error'
      });
    } finally {
      setIsPublishing(false);
    }
  }

  const handlePublish = async ({force = false}: {force?: boolean} = {}) => {
    if (!isApproved && !force) {
      setShowForcePublishConfirmation(true);
      return;
    }

    setIsPublishing(true);
    try {
      const result = await publishDistroReview({
        reviewId,
      });

      if ('status' in result && result.status === 'ok') {
        toast({
          description: 'Survey published successfully',
          variant: 'success',
          duration: 5000
        });
        // Refresh the review data to show updated status
        const updatedReview = await getDistroReview({ reviewId });
        if (!('error' in updatedReview)) {
          setReviewData(updatedReview);
        }
      } else if ('error' in result) {
        toast({
          description: result.error,
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Error publishing survey:', error);
      toast({
        description: 'Failed to publish survey',
        variant: 'error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = async () => {
    setIsClosing(true);
    setShowCloseConfirmation(false);
    try {
      const result = await closeDistroReview({ reviewId });

      if (!result.error) {
        toast({
          description: 'Review closed successfully',
          variant: 'success',
          duration: 5000
        });
        // Refresh the review data to show updated status
        const updatedReview = await getDistroReview({ reviewId });
        if (!('error' in updatedReview)) {
          setReviewData(updatedReview);
        }
      }
    } catch (error) {
      console.error('Error closing review:', error);
      toast({
        description: 'Failed to close review',
        variant: 'error'
      });
    } finally {
      setIsClosing(false);
    }
  };

  const handleCloseReviewClick = useCallback(() => {
    setShowCloseConfirmation(true);
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return <Spinner className="p-8" />;
    }

    return (
      <div className='bg-gray-50'>
        {surveyHashMismatch && (isPendingReview || isApproved) && (
          <Alert
            title="Conflict Detected"
            variant="danger"
            className="mb-6"
          >
            The survey has been saved outside of the review flow.
            <br/>If you publish this review, <b>it will overwrite recent changes</b>.
            {reviewData?.currentSurveyHash && currentSurveyHash && (
              <div className="mt-2 text-sm">
                <div>Review based on: {reviewData.currentSurveyHash.slice(0, 8)}...</div>
                <div>Current survey: {currentSurveyHash.slice(0, 8)}...</div>
              </div>
            )}
          </Alert>
        )}

        {hasNotificationChanges && (isPendingReview || isApproved) && (
          <Alert
            title="Notification Review Required"
            variant="warning"
            className="mb-6"
          >
            This commit includes changes that affect notifications.
            Please review the <button
              className="text-blue-600 underline cursor-pointer bg-transparent border-0 p-0 font-inherit"
              onClick={() => setHashForView('notifications')}
            >
              Notifications tab
            </button> to verify these changes before publishing.
          </Alert>
        )}
        
        <DistroReviewDescription
          reviewData={reviewData}
          reviewId={reviewId!}
          onUpdate={refreshReviewData}
          collapseDescription={currentView !== 'summary'}
        />
        
        {currentView === 'summary' ? (
          <DistroReviewSummary
            reviewData={reviewData}
            reviewId={reviewId!}
            isClosing={isClosing}
            isPublishing={isPublishing}
            onCloseReviewClick={handleCloseReviewClick}
            onUpdate={refreshReviewData}
            surveyHashMismatch={surveyHashMismatch}
          />
        ) : currentView === 'changes' ? (
          <ChangesWorkflowReview
            distroCommitId={reviewData?.commitId || ''}
            surveyHashMismatch={surveyHashMismatch}
            isPendingReview={isPendingReview}
            isApproved={isApproved}
            createdBy={reviewData?.createdBy || ''}
          />
        ) : currentView === 'target_fields' ? (
          <TargetFieldsWorkflowReview distroCommitId={reviewData?.commitId || ''} />
        ) : currentView === 'notifications' ? (
          <NotificationsWorkflowReview distroCommitId={reviewData?.commitId || ''} />
        ) : (
          <div>Unknown view: {currentView}</div>
        )}
      </div>
    );
  };

  return (
    <SidebarNavProvider>
      <div className="flex h-screen w-full">
        <SidebarNav>
          <SidebarNavContent>
            <Heading level="h1" size="h3" className="mb-2">Review #{reviewId}</Heading>
            {isLoading ? (
              <Spinner className="p-8" />
            ) : (<>
              <Heading level="h2" size="h5" className="mb-4">{reviewData?.surveyName}</Heading>
              {!collabEnabled && reviewData?.commitId && (
                <div className="mb-4">
                  {reviewData?.commitId && <div className="text-gray-500 text-sm">Commit {reviewData.commitId}</div>}
                  <a
                    href={`/config/${reviewData.surveyName}?commit=${reviewData.commitId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 no-underline"
                  >
                    Open Editor {surveyHashMismatch ? <span className="text-red-500 pl-1"> (conflict)</span> : ''}
                  </a>
                </div>
              )}
              <div className="mb-4">
                {isPublished ? (
                  <div className="flex flex-col gap-1">
                    <div className="bg-purple-100 text-purple-800 font-medium py-1 px-3 rounded-full inline-flex items-center">
                      <Icon name="CheckCircleIcon" className="w-5 h-5 mr-1 text-purple-900" type='solid' />
                      Published
                    </div>
                    <div className="text-sm text-purple-900">{reviewData.publishedAt && new Date(reviewData.publishedAt).toLocaleString()}</div>
                    <div className="text-sm text-purple-900">{reviewData.publishedBy && <> by {reviewData.publishedBy}</>}</div>
                    {reviewData.publishedWithoutApproval && (
                      <div className="text-sm text-orange-700 flex items-center">
                        <Icon name="ExclamationTriangleIcon" className="w-4 h-4 mr-1 text-orange-700" type='solid' />
                        Published without approval
                      </div>
                    )}
                  </div>
                ) : isClosed ? (
                  <div className="flex flex-col gap-1">
                    <div className="bg-gray-100 text-gray-800 font-medium py-1 px-3 rounded-full inline-flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                      </svg>
                      Closed
                    </div>
                    <div className="text-sm text-gray-700">{reviewData.closedAt && new Date(reviewData.closedAt).toLocaleString()}</div>
                    <div className="text-sm text-gray-700">{reviewData.closedBy && <> by {reviewData.closedBy}</>}</div>
                  </div>
                ) : isPendingReview ? (
                  <>
                    <div className="bg-yellow-100 text-yellow-800 font-medium py-1 px-3 rounded-full inline-flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                      </svg>
                      Pending Review
                    </div>
                    <div></div>
                  </>
                ) : isApproved && (
                  <div className="bg-green-100 text-green-800 font-medium py-1 px-3 rounded-full inline-flex items-center">
                    <Icon name="CheckCircleIcon" className="w-5 h-5 mr-1 text-green-900" type='solid' />
                    Approved
                  </div>
                )}
              </div>
              <SidebarNavMenu>
                <SidebarNavMenuItem>
                  <SidebarNavMenuButton
                    isActive={currentView === 'summary'}
                    onClick={() => setHashForView('summary')}
                    className='cursor-pointer flex items-center gap-2'
                  >
                    Summary
                  </SidebarNavMenuButton>
                </SidebarNavMenuItem>
                <SidebarNavMenuItem>
                  <SidebarNavMenuButton
                    isActive={currentView === 'changes'}
                    onClick={() => setHashForView('changes')}
                    className='cursor-pointer flex items-center gap-2 justify-between'
                  >
                    <span>Changes</span>
                    {reviewData?.workflowStatuses && (
                      getStatusIcon(getWorkflowStatus(reviewData.workflowStatuses, 'changes'))
                    )}
                  </SidebarNavMenuButton>
                </SidebarNavMenuItem>
                <SidebarNavMenuItem>
                  <SidebarNavMenuButton
                    isActive={currentView === 'notifications'}
                    onClick={() => setHashForView('notifications')}
                    className={'cursor-pointer flex items-center gap-2 justify-between' + (hasNotificationChanges && (isPendingReview || isApproved) ? ' ring-2 ring-yellow-400 ring-offset-1' : '')}
                  >
                    <span className="flex items-center gap-1">
                      Notifications
                      {hasNotificationChanges && (isPendingReview || isApproved) && (
                        <Icon name="ExclamationTriangleIcon" type="solid" className="w-4 h-4 text-yellow-500" />
                      )}
                    </span>
                    {reviewData?.workflowStatuses && (
                      getStatusIcon(getWorkflowStatus(reviewData.workflowStatuses, 'notifications'))
                    )}
                  </SidebarNavMenuButton>
                </SidebarNavMenuItem>
                <SidebarNavMenuItem>
                  <SidebarNavMenuButton
                    isActive={currentView === 'target_fields'}
                    onClick={() => setHashForView('target_fields')}
                    className='cursor-pointer flex items-center gap-2 justify-between'
                  >
                    <span>Target Fields</span>
                    {reviewData?.workflowStatuses && (
                      getStatusIcon(getWorkflowStatus(reviewData.workflowStatuses, 'target_fields'))
                    )}
                  </SidebarNavMenuButton>
                </SidebarNavMenuItem>
              </SidebarNavMenu>
              <div className="mb-2 flex flex-col justify-end items-center h-full">
                {surveyHashMismatch && (isPendingReview || isApproved) && (<span className="text-red-500 pb-2 font-bold"> (conflict detected)</span>)}
                {isPendingReview && (
                  <Button
                    onClick={handleApprove}
                    isDisabled={isPublishing || isClosing}
                    loading={isPublishing}
                    variant="primary"
                    className="w-full mb-2"
                  >
                    Approve Review
                  </Button>
                )}
                {(isPendingReview || isApproved) && (
                  <>
                    <Button
                      onClick={() => handlePublish({ force: false })}
                      isDisabled={isPublishing || isClosing}
                      loading={isPublishing}
                      variant={isApproved ? "primary" : "danger"}
                      className="w-full"
                    >
                      {`Publish Survey ${isApproved ? '' : 'without Approval'}`}
                    </Button>
                  </>
                )}
              </div>
            </>
            )}
          </SidebarNavContent>
        </SidebarNav>
        <main id="main-content" className="flex-1 overflow-auto p-6 w-full bg-gray-50">
          {renderContent()}
        </main>
      </div>

      <Modal
        open={showForcePublishConfirmation}
        onClose={setShowForcePublishConfirmation}
        title="Force Publish Survey"
        size="sm"
        icon={<Icon name="ExclamationTriangleIcon" type="solid" />}
      >
        <p className="mb-4">
          Are you sure you want to publish this review without approval? This action cannot be undone.
          We strongly recommend that you seek approval from a reviewer before publishing.
        </p>
        <div className="flex flex-row justify-end gap-x-3">
          <Button
            variant="secondary"
            onClick={() => setShowForcePublishConfirmation(false)}
            isDisabled={isPublishing}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              handlePublish({ force: true });
              setShowForcePublishConfirmation(false);
            }}
            loading={isPublishing}
            isDisabled={isPublishing}
          >
            Force Publish
          </Button>
        </div>
      </Modal>

      <Modal
        open={showCloseConfirmation}
        onClose={setShowCloseConfirmation}
        title="Close Review"
        size="sm"
        icon={<Icon name="ExclamationTriangleIcon" type="solid" />}
      >
        <p className="mb-4">
          Are you sure you want to close this review? This action cannot be undone.
          The review will be marked as closed and will no longer be available for publishing.
        </p>
        <div className="flex flex-row justify-end gap-x-3">
          <Button
            variant="secondary"
            onClick={() => setShowCloseConfirmation(false)}
            isDisabled={isClosing}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleClose}
            loading={isClosing}
            isDisabled={isClosing}
          >
            Close Review
          </Button>
        </div>
      </Modal>
    </SidebarNavProvider>
  );
}
